import { useEffect, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import axios from "axios";
import { CheckCircle2, Clock, FileDown, XCircle } from "lucide-react";
import { IMAGES } from "../data/content";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const Shell = ({ children }) => (
  <div data-testid="payment-page" className="min-h-screen bg-neutral-950 text-neutral-100 flex flex-col">
    <header className="border-b border-neutral-800 h-16 flex items-center px-6">
      <Link to="/" className="flex items-center gap-3">
        <span className="w-3 h-3 bg-red-600 inline-block" />
        <img src={IMAGES.logoWhite} alt="Legend Team" className="h-6 w-auto" />
      </Link>
    </header>
    <main className="flex-1 flex items-center justify-center px-6 py-16">
      <div className="max-w-lg w-full border border-neutral-800 bg-neutral-900 p-10 text-center">{children}</div>
    </main>
  </div>
);

const BackHome = () => (
  <Link
    to="/"
    data-testid="payment-back-home"
    className="inline-block mt-8 px-6 py-3 border border-neutral-700 hover:border-red-600 text-xs font-bold uppercase tracking-widest text-neutral-200 hover:text-red-500 transition-colors"
  >
    Torna al sito
  </Link>
);

export default function PaymentSuccess() {
  const [params] = useSearchParams();
  const sessionId = params.get("session_id");
  const [state, setState] = useState("polling");
  const [ricevutaUrl, setRicevutaUrl] = useState(null);

  useEffect(() => {
    if (!sessionId) {
      setState("error");
      return;
    }
    let attempts = 0;
    let timer;
    const poll = async () => {
      try {
        const r = await axios.get(`${API}/payments/status/${sessionId}`);
        if (r.data.payment_status === "paid") {
          if (r.data.ricevuta_url) setRicevutaUrl(`${process.env.REACT_APP_BACKEND_URL}${r.data.ricevuta_url}`);
          return setState("paid");
        }
        if (["expired", "failed"].includes(r.data.payment_status)) return setState("error");
      } catch (e) {
        /* riprova */
      }
      attempts += 1;
      if (attempts >= 8) return setState("timeout");
      timer = setTimeout(poll, 2000);
    };
    poll();
    return () => clearTimeout(timer);
  }, [sessionId]);

  return (
    <Shell>
      {state === "polling" && (
        <div data-testid="payment-status-polling">
          <Clock className="w-12 h-12 text-red-500 mx-auto animate-pulse" />
          <h1 className="font-display text-2xl font-black uppercase mt-6">Verifica del pagamento in corso</h1>
          <p className="text-sm text-neutral-400 mt-3">Attendi qualche secondo, stiamo confermando la transazione con Stripe.</p>
        </div>
      )}
      {state === "paid" && (
        <div data-testid="payment-status-paid">
          <CheckCircle2 className="w-12 h-12 text-red-500 mx-auto" />
          <h1 className="font-display text-2xl font-black uppercase mt-6">Tesseramento completato</h1>
          <p className="text-sm text-neutral-300 mt-3 leading-relaxed">
            Pagamento confermato. Abbiamo inviato la pratica a info@legendteam.it e una ricevuta al tuo indirizzo email.
            La segreteria ti contatterà per il rilascio della tessera CONI.
          </p>
          {ricevutaUrl && (
            <a
              data-testid="ricevuta-download"
              href={ricevutaUrl}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-2 mt-8 px-6 py-3 bg-red-600 hover:bg-red-500 text-white text-xs font-black uppercase tracking-widest transition-colors"
            >
              <FileDown className="w-4 h-4" /> Scarica la ricevuta PDF
            </a>
          )}
          <br />
          <BackHome />
        </div>
      )}
      {state === "timeout" && (
        <div data-testid="payment-status-timeout">
          <Clock className="w-12 h-12 text-neutral-400 mx-auto" />
          <h1 className="font-display text-2xl font-black uppercase mt-6">Conferma in elaborazione</h1>
          <p className="text-sm text-neutral-400 mt-3">
            Il pagamento risulta ancora in elaborazione. Riceverai un'email appena confermato; per dubbi scrivi a info@legendteam.it.
          </p>
          <BackHome />
        </div>
      )}
      {state === "error" && (
        <div data-testid="payment-status-error">
          <XCircle className="w-12 h-12 text-neutral-500 mx-auto" />
          <h1 className="font-display text-2xl font-black uppercase mt-6">Pagamento non confermato</h1>
          <p className="text-sm text-neutral-400 mt-3">Non è stato possibile verificare la transazione. Nessun addebito è stato effettuato.</p>
          <BackHome />
        </div>
      )}
    </Shell>
  );
}

export function PaymentCancel() {
  return (
    <Shell>
      <div data-testid="payment-status-cancel">
        <XCircle className="w-12 h-12 text-neutral-500 mx-auto" />
        <h1 className="font-display text-2xl font-black uppercase mt-6">Pagamento annullato</h1>
        <p className="text-sm text-neutral-400 mt-3">
          Il tesseramento non è stato completato e non è stato effettuato alcun addebito. Puoi riprovare quando vuoi.
        </p>
        <BackHome />
      </div>
    </Shell>
  );
}
